'use client';
import React, { useMemo } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '@benzinga/core-ui';
import SeeMoreLink from '../../../app/(site)/quote/[ticker]/_components/SeeMoreLink';
import { seeMoreDestinations } from '../../../app/(site)/quote/[ticker]/_utils/seeMoreDestinations';
import { numberShorthand } from '@benzinga/utils/numberUtils';
import type { ColumnDef } from '@benzinga/table';
import type { InsidersTradeFiling } from '@benzinga/insider-trades-manager';
import { formatDate, formatTradeValue } from './formatters';
import QuoteListTable from './QuoteListTable';
import { TradeTypeBadge, insiderTradeTypeTone } from './TradeTypeBadge';

const insiderFilingsColumnsDef: ColumnDef<InsidersTradeFiling>[] = [
  {
    cellClass: 'text-gray-600',
    field: 'first_trade_date',
    headerName: 'Date',
    valueFormatter: ({ data }) => formatDate(data.first_trade_date),
  },
  {
    cellClass: 'font-semibold text-gray-800',
    field: 'insider_names',
    headerName: 'Insider',
    valueFormatter: ({ data }) => data.insider_names || '—',
  },
  {
    cellClass: 'max-w-[160px] truncate text-gray-500',
    field: 'insider_titles_unique',
    headerName: 'Title',
    valueFormatter: ({ data }) => data.insider_titles_unique || data.insider_titles || '—',
  },
  {
    cellRenderer: ({ data }) => (
      <TradeTypeBadge label={data.trade_status} tone={insiderTradeTypeTone(data.trade_status)} />
    ),
    field: 'trade_status',
    headerName: 'Type',
  },
  {
    cellClass: 'text-right text-gray-700',
    field: 'traded_shares',
    headerClass: 'text-right',
    headerName: 'Shares',
    valueFormatter: ({ data }) => (data.traded_shares ? numberShorthand(Math.abs(data.traded_shares)) : '—'),
  },
  {
    cellClass: 'text-right text-gray-700',
    field: 'traded_share_price',
    headerClass: 'text-right',
    headerName: 'Price',
    valueFormatter: ({ data }) =>
      data.traded_share_price ? `$${Number(data.traded_share_price).toFixed(2)}` : '—',
  },
  {
    cellClass: 'text-right font-semibold text-gray-800',
    field: 'traded_value',
    headerClass: 'text-right',
    headerName: 'Value',
    valueFormatter: ({ data }) => (data.traded_value ? formatTradeValue(Math.abs(data.traded_value)) : '—'),
  },
];

export const InsiderFilingsTable: React.FC<{
  companyName: string;
  filings: InsidersTradeFiling[];
  symbol: string;
}> = ({ companyName, filings, symbol }) => {
  const { t } = useTranslation('quote');
  const sixMonthFilings = useMemo(() => {
    const cutoff = DateTime.now().minus({ months: 6 });
    const filingDate = (filing: InsidersTradeFiling) => filing.first_trade_date || filing.last_trade_date || '';
    return filings
      .filter(filing => {
        const date = filingDate(filing);
        return date ? DateTime.fromISO(date) >= cutoff : false;
      })
      .slice()
      .sort((a, b) => filingDate(b).localeCompare(filingDate(a)))
      .slice(0, 50);
  }, [filings]);

  const companyCik = useMemo(() => filings.find(filing => filing.company_cik)?.company_cik, [filings]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <SectionTitle level={2} size="xl" uppercase={false}>
          {companyName} Insider Transactions (Last 6 Months)
        </SectionTitle>
        {sixMonthFilings.length > 0 && (
          <SeeMoreLink
            action="see_more_insider_trades"
            href={seeMoreDestinations.insiderTrades(symbol, companyName, companyCik)}
            label={t('Quote.SeeMore.insider-trades')}
          />
        )}
      </div>
      {sixMonthFilings.length > 0 ? (
        <QuoteListTable
          columnsDef={insiderFilingsColumnsDef}
          gateType="insider-trades"
          noDataText="No insider filings in the last 6 months."
          rowData={sixMonthFilings}
        />
      ) : (
        <p className="text-sm text-gray-400">No insider filings in the last 6 months.</p>
      )}
    </div>
  );
};

export default InsiderFilingsTable;
